// =============================================================================
// ColyseusClient — wrapper over `colyseus.js`. Handles:
//   - joinOrCreate / joinById with `{ token }` injection
//   - Per-room reconnection-token persistence (localStorage)
//   - Auto-reconnect attempt within 30 s on unconsented disconnect
//   - WebSocket close-code handling (4000 invalid_token, 4001 kicked, 4213 mm)
//
// Each app should hold ONE ColyseusClient and subscribe rooms to it.
// =============================================================================

import { Client, Room } from "colyseus.js";
import { CLOSE_CODE } from "@game/types";

const RECONNECT_KEY = "digimon:reconnect:v1";
const RECONNECT_TIMEOUT_MS = 30_000;

export type RoomName = "hello" | "global" | "world_zone" | "battle" | "pvp_battle" | "party" | "trade";

export interface JoinOptions {
  /** Required for every room except `hello`. */
  token?: string;
  /** zoneId for world_zone, partyId for party, ranked for pvp, etc. */
  [k: string]: unknown;
}

export interface ReconnectionTokens {
  [roomKey: string]: string;
}

export interface ColyseusClientOptions {
  url: string;
  /** Called when a close code maps to a forced re-auth (4000). */
  onAuthFailed?: () => void;
  /** Called when reconnect fails after the 30 s window. */
  onReconnectFailed?: (roomName: RoomName) => void;
}

export class ColyseusClient {
  private readonly client: Client;
  private opts: ColyseusClientOptions;

  constructor(opts: ColyseusClientOptions) {
    this.opts = opts;
    this.client = new Client(opts.url);
  }

  /** Direct access for advanced cases (e.g. listing rooms). */
  raw(): Client {
    return this.client;
  }

  setOptions(opts: Partial<ColyseusClientOptions>): void {
    this.opts = { ...this.opts, ...opts };
  }

  // ---- joins ----------------------------------------------------------

  /**
   * Join (or create) a room and capture its reconnection token. The returned
   * Room instance has `.onLeave` wired to attempt a 30 s reconnect; pass your
   * handlers via `onReconnect` to re-attach state callbacks.
   */
  async joinOrCreate<S>(
    name: RoomName,
    options: JoinOptions,
    cbs: {
      onReconnect?: (room: Room<S>) => void;
      onLost?: (code: number) => void;
    } = {},
  ): Promise<Room<S>> {
    const room = await this.client.joinOrCreate<S>(name, options as Record<string, unknown>);
    this.attachLifecycle(name, room, options, cbs);
    return room;
  }

  async joinById<S>(
    roomId: string,
    options: JoinOptions,
    cbs: {
      onReconnect?: (room: Room<S>) => void;
      onLost?: (code: number) => void;
    } = {},
  ): Promise<Room<S>> {
    const room = await this.client.joinById<S>(roomId, options as Record<string, unknown>);
    this.attachLifecycle(roomId, room, options, cbs);
    return room;
  }

  /** Stop reconnect tracking for a room (call when leaving voluntarily). */
  forget(key: string): void {
    const tokens = loadTokens();
    delete tokens[key];
    saveTokens(tokens);
  }

  clearAllTokens(): void {
    saveTokens({});
  }

  // ---- internal -------------------------------------------------------

  private attachLifecycle<S>(
    key: string,
    room: Room<S>,
    options: JoinOptions,
    cbs: { onReconnect?: (room: Room<S>) => void; onLost?: (code: number) => void },
  ): void {
    saveToken(key, room.reconnectionToken);

    room.onLeave(async (code) => {
      // 4000 invalid_token → force re-auth
      if (code === CLOSE_CODE.INVALID_TOKEN) {
        this.forget(key);
        this.opts.onAuthFailed?.();
        cbs.onLost?.(code);
        return;
      }
      // Voluntary leave (1000) — don't try to reconnect
      if (code === 1000) {
        this.forget(key);
        return;
      }
      // 4001 kicked from party / 4213 matchmaker error — propagate
      if (code === CLOSE_CODE.KICKED_FROM_PARTY || code === CLOSE_CODE.MATCHMAKER_ERROR) {
        this.forget(key);
        cbs.onLost?.(code);
        return;
      }
      // Any other close: try to reconnect with the saved token within 30 s
      const stored = loadTokens()[key];
      if (!stored) {
        cbs.onLost?.(code);
        return;
      }
      try {
        const reconnected = await Promise.race<Room<S>>([
          this.client.reconnect(stored) as unknown as Promise<Room<S>>,
          new Promise<Room<S>>((_, reject) =>
            setTimeout(() => reject(new Error("reconnect_timeout")), RECONNECT_TIMEOUT_MS),
          ),
        ]);
        // Re-arm lifecycle on the new instance
        this.attachLifecycle(key, reconnected, options, cbs);
        cbs.onReconnect?.(reconnected);
      } catch {
        this.forget(key);
        this.opts.onReconnectFailed?.(key as RoomName);
        cbs.onLost?.(code);
      }
    });
  }
}

// ---- token storage --------------------------------------------------

function loadTokens(): ReconnectionTokens {
  try {
    const raw = localStorage.getItem(RECONNECT_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as ReconnectionTokens;
  } catch {
    return {};
  }
}

function saveTokens(tokens: ReconnectionTokens): void {
  try {
    localStorage.setItem(RECONNECT_KEY, JSON.stringify(tokens));
  } catch {
    // ignore
  }
}

function saveToken(key: string, token: string): void {
  const tokens = loadTokens();
  tokens[key] = token;
  saveTokens(tokens);
}
